import React from 'react';
import { useSelector } from 'react-redux';
import { TOwnershipStructure } from './types.ts';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { TIdentificationForm, TUpdatePayload } from '~/store/identification-form/types.ts';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.ts';
import { useOwnershipStructureForm } from './hooks/useOwnershipStructureForm.ts';
import { Box, Button } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices.ts';
import { cloneDeep } from 'lodash';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { B2fullCheck, trimValues } from '../../utils.ts';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import OwnerPersonInfo from './components/OwnerPersonInfo.tsx';
import { gerParsedValues } from './utils/utils.tsx';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';
import { updateAdditionalInformation } from '~/store/identification-form/thunks/additionalInformationThunks.ts';

interface IOwnershipStructureProps {
  onNextStep: (isNextDisabled?: boolean) => void;
}

function OwnershipStructure({ onNextStep }: IOwnershipStructureProps) {
  const dispatch = useAppDispatch();
  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);

  const form = useOwnershipStructureForm();
  const { isSubmitting, isDirty } = form.formState;

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TOwnershipStructure) => {
      try {
        const parsedValues: TOwnershipStructure = gerParsedValues(values);
        const isBeneficiariesAbsent = !parsedValues.owners.some((item) => item.isBeneficiary);

        if (isDirty) {
          const data: TUpdatePayload = {
            applicationId: appId as number,
            ownershipStructure: trimValues({ ...parsedValues, isBeneficiariesAbsent: undefined }),
          };

          try {
            const callbackFunc = async () => {
              await dispatch(updateAdditionalInformation({ applicationId: appId! }));
            };
            await dispatch(
              updateForm({
                data,
                reportId: IdentificationFormSteps.ownershipStructure,
                callbackFunc,
              })
            );
            await B2fullCheck(appId || '', IdentificationFormSteps.ownershipStructure);
            const formData = {
              field: IdentificationFormStepNames.ownershipStructure as keyof TIdentificationForm,
              data: cloneDeep({
                ...parsedValues,
                isBeneficiariesAbsent,
              }),
            };

            dispatch(updateFormData(formData));
            dispatch(updateFormDataBack(formData));
            !disableGoNext && onNextStep(isBeneficiariesAbsent);
          } catch (error) {
            console.error(error);
          }
        } else {
          !disableGoNext && onNextStep(isBeneficiariesAbsent);
        }
      } catch (error) {
        console.error(error);
      }
    };

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
        <Box sx={{ ...commonStyles.columnFlexContainer, gap: 3 }}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <OwnerPersonInfo />
          <Box sx={{ width: '50%' }}>
            <FormInput
              name="ownershipPercentageTotal"
              disabled
              placeholder="Введіть частку в статутному капіталі"
              label="Загальна частка всіх учасників у статутному капіталі (%)"
              fullWidth
            />
          </Box>
          <Button
            disabled={isSubmitting}
            type="submit"
            variant="contained"
            sx={{ alignSelf: 'center' }}
          >
            Продовжити
          </Button>
        </Box>
        {isSubmitting && <FullPageLoader />}
      </Box>
    </FormProvider>
  );
}
export default React.memo(OwnershipStructure);
